import { z } from "zod";

/**
 * T13-13: the POS CSP has `script-src 'self'` (no 'unsafe-eval'). Zod 4 probes `Function("")`
 * once to decide whether it may JIT-compile object parsers — under that CSP the probe is blocked
 * and reported as a violation (it then falls back to the plain parser). `jitless` skips the probe;
 * it is global, so it is set here, in the first module that parses anything.
 */
z.config({ jitless: true });

const flag = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform((v) => v === true || v === "true" || v === "1");

export const envSchema = z.object({
  /** Gateway prefix for POS APIs (`/api/v1/pos/*` → pos-service, `/api/v1/pos/auth/*` → identity). */
  VITE_API_BASE_URL: z.string().default("/api/v1"),
  /** The register's push channel (pos-service `GET /v1/pos/events/stream`, T07-06). */
  VITE_POS_EVENTS_PATH: z.string().default("/api/v1/pos/events/stream"),
  /** R21 realtime hub (T07-40): `GET {base}/stream`, `POST {base}/subscriptions`. */
  VITE_REALTIME_BASE_URL: z.string().default("/api/v1/realtime"),
  /** inventory-service's availability stream for registers (T03-17). */
  VITE_POS_INVENTORY_STREAM_PATH: z.string().default("/api/v1/pos/inventory/stream"),
  /** Dev-only screens (`/_dev/components` gallery for the a11y check). */
  VITE_POS_DEV_TOOLS: flag,
  /** Shown in 設定 (the image build sets it to the release / git sha). */
  VITE_APP_VERSION: z.string().default("dev"),
});

export type Env = z.infer<typeof envSchema>;

export function parseEnv(raw: Record<string, string | boolean | undefined>): Env {
  const result = envSchema.safeParse(raw);
  if (!result.success) {
    const lines = result.error.issues.map((i) => `  ${i.path.join(".")}: ${i.message}`);
    throw new Error(`Invalid pos environment:\n${lines.join("\n")}`);
  }
  return result.data;
}

export const env: Env = parseEnv({
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_POS_EVENTS_PATH: import.meta.env.VITE_POS_EVENTS_PATH,
  VITE_REALTIME_BASE_URL: import.meta.env.VITE_REALTIME_BASE_URL,
  VITE_POS_INVENTORY_STREAM_PATH: import.meta.env.VITE_POS_INVENTORY_STREAM_PATH,
  VITE_POS_DEV_TOOLS: import.meta.env.VITE_POS_DEV_TOOLS ?? import.meta.env.DEV,
  VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
});

/** `/api/v1` → `/api`: the generated client paths carry `/v1` themselves. */
export function apiOrigin(base: string = env.VITE_API_BASE_URL): string {
  return base.replace(/\/v1\/?$/, "");
}
